import {
  Combobox,
  createListCollection,
  Portal,
  useCombobox,
} from "@ark-ui/react"
import cx from "classnames"
import { nanoid } from "nanoid"
import type React from "react"
import {
  Fragment,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react"
import { getA11yOnClick } from "../../../helpers"
import { useOnMountEffect } from "../../../hooks/useOnMountEffect"
import { CheckIcon, Icon, type IconSources } from "../../Icon"
import type { FormElement } from "../form"
import { Hint } from "../Hint"

type AutocompleteItemBaseKeys = "icon" | "label" | "value" | "disabled"

export type AutocompleteItemInput<T extends object = Record<string, unknown>> =
  | string
  | ({
      icon?: IconSources
      label?: string
      value: string
      disabled?: boolean
    } & Partial<Omit<T, AutocompleteItemBaseKeys>>)

type AutocompleteItemAllBaseKeys = AutocompleteItemBaseKeys | "description"

export type AutocompleteItem<T extends object = Record<string, unknown>> = {
  icon?: IconSources
  label: string
  value: string
  disabled?: boolean
  description?: string
} & Omit<T, AutocompleteItemAllBaseKeys>

export type AutocompleteRefType<T extends object = Record<string, unknown>> = {
  query: string
  input: HTMLInputElement
  open: () => void
  focus: () => void
  clearValue: () => void
  setSelectedItem: (item: AutocompleteItemInput<T>) => void
}

type Props<T extends object = Record<string, unknown>> = {
  className?: string
  label?: string
  hint?: string
  fullWidth?: boolean
  icon?: IconSources
  items: AutocompleteItemInput<T>[]
  popoverClassName?: string
  minQueryLength?: number
  autoFilter?: boolean
  focusOnInit?: boolean
  autocomplete?: boolean
  autocompleteRef?: React.RefObject<HTMLDivElement>
  onClearValue?: () => void
  onKeyDown?: (event: React.KeyboardEvent<HTMLElement>) => void
  onQueryChange?: (term: string) => void
  onSelectItem?: (item: AutocompleteItem<T>, term: string) => boolean
  renderItem?: (item: AutocompleteItem<T>, term: string) => React.ReactNode
  allowCustomValue?: boolean
} & FormElement &
  React.InputHTMLAttributes<HTMLInputElement>

function sanitizeItem<T extends object = Record<string, unknown>>(
  item: AutocompleteItemInput<T>,
): AutocompleteItem<T> {
  if (typeof item === "string") {
    // If the item is a string, create an object with label and value being the string
    return { label: item, value: item } as AutocompleteItem<T>
  } else {
    // If the item is an object, ensure it has a label property
    const { value, label } = item
    return { ...item, label: label ?? value } as AutocompleteItem<T>
  }
}

function sanitizeItems<T extends object = Record<string, unknown>>(
  items: AutocompleteItemInput<T>[],
): AutocompleteItem<T>[] {
  return items.map((item) => sanitizeItem(item))
}

const HIGHLIGHT_CLASSNAME = "autocomplete_matching_query"
const regExpEscape = (s: string) => s.replace(/[-\\^$*+?.()|[\]{}]/g, "\\$&")

type AutocompleteMatchHighlightPropsType = {
  label: string
  query: string
  matchClassName?: string
}

export const AutocompleteMatchHighlight = ({
  label,
  query,
  matchClassName = HIGHLIGHT_CLASSNAME,
}: AutocompleteMatchHighlightPropsType) => {
  if (query.trim().length === 0) return label

  const queryRX = RegExp(`(${regExpEscape(query)})`, "gi")

  const parts = label.split(queryRX)

  return (
    <>
      {parts.map((part, index) => {
        const key = `${index}-${part}`
        return part.match(queryRX) ? (
          <span className={cx(matchClassName)} key={key}>
            {part}
          </span>
        ) : (
          <Fragment key={key}>{part}</Fragment>
        )
      })}
    </>
  )
}

const KEY_CODE_ENTER = 13

const ignoreItemValue = nanoid()

const _Autocomplete = forwardRef(
  (
    {
      id,
      className,
      label,
      hint,
      fullWidth,
      icon,
      status,
      focusOnInit = false,
      popoverClassName,
      minQueryLength = 1,
      onQueryChange,
      onSelectItem,
      renderItem,
      items,
      onKeyDown,
      disabled,
      defaultValue,
      onClearValue,
      allowCustomValue = true,
      ...inputProps
    }: Props,
    ref,
  ) => {
    const [defaultInputValue, setDefaultInputValue] = useState(defaultValue)
    const [autocompleteKey, setAutocompleteKey] = useState(true)

    const isInputValidChangedProgrammatically = useRef(false)
    const sanitizedInitialItems = useMemo(() => sanitizeItems(items), [items])

    const [value, setValue] = useState<string>((defaultValue as string) || "")
    const [popoverItems, setPopoverItems] = useState(sanitizedInitialItems)
    // Allow to use the combobox without listening its changes
    const comboboxRef = useRef<ReturnType<
      typeof useCombobox<AutocompleteItem<object>>
    > | null>(null)

    const collection = useMemo(
      () =>
        createListCollection({
          items: popoverItems.length
            ? popoverItems
            : sanitizeItems([{ value: ignoreItemValue }]), // having an empty collection makes the component buggy. Best practice is to always have an "instruction" item when empty. This dummy item will not be displayed
        }),
      [popoverItems],
    )

    const handleInputChange = (details: Combobox.InputValueChangeDetails) => {
      if (isInputValidChangedProgrammatically.current) {
        isInputValidChangedProgrammatically.current = false
        return
      }

      onQueryChange
        ? onQueryChange(details.inputValue)
        : setPopoverItems(
            sanitizedInitialItems.filter((item) =>
              item.label
                .toLowerCase()
                .includes(details.inputValue.toLowerCase()),
            ),
          )
    }

    useEffect(() => {
      setPopoverItems(sanitizeItems(items))
    }, [items])

    const inputRef = useRef<HTMLInputElement>(null)

    const combobox = useCombobox({
      ...(id ? { id } : {}),
      allowCustomValue: allowCustomValue,
      autoFocus: focusOnInit,
      value: [value],
      collection: collection,
      onInputValueChange: handleInputChange,
      openOnClick: minQueryLength === 0 && collection.items.length > 0,
      onValueChange: (details) => {
        isInputValidChangedProgrammatically.current = true
        details.items[0] ? setValue(details.items[0].value) : setValue("")
      },
      onInteractOutside: () => {
        setTimeout(() => inputRef.current?.blur(), 30)
      },
      positioning: {
        gutter: -3,
        flip: false,
      },
    })

    useEffect(() => {
      comboboxRef.current = combobox
    }, [combobox])

    useImperativeHandle(ref, (): AutocompleteRefType => {
      return {
        query: combobox.inputValue,
        input: inputRef.current as HTMLInputElement,
        open: () => {
          combobox.setOpen(true)
        },
        focus: () => {
          combobox.focus()
        },
        clearValue: () => {
          combobox.clearValue()
          setValue("")
          setDefaultInputValue("")
          onClearValue?.()
          onQueryChange?.("")
        },
        setSelectedItem: (selectedItem: AutocompleteItemInput) => {
          isInputValidChangedProgrammatically.current = true
          const sanitizedItem = sanitizeItem(selectedItem)
          const existingItem = popoverItems.find(
            (item) => item.value === sanitizedItem.value,
          )

          if (!existingItem) {
            combobox.setOpen(false)
            setPopoverItems([])
            setDefaultInputValue(sanitizedItem.value)
            setValue(sanitizedItem.value)
            setAutocompleteKey(!autocompleteKey)
          } else {
            setValue(sanitizedItem.value)
            combobox.setOpen(false)
          }
        },
      }
    }, [combobox, autocompleteKey, onClearValue, onQueryChange, popoverItems])

    useOnMountEffect(() => {
      focusOnInit && combobox.setOpen(collection.items.length > 0)
    })

    const validItems = useMemo(
      () => collection.items.filter((item) => item.value !== ignoreItemValue),
      [collection.items],
    )

    useEffect(() => {
      if (validItems.length) {
        if (!comboboxRef.current?.open && comboboxRef.current?.focused) {
          comboboxRef.current.setOpen(true)
        }
      } else {
        if (comboboxRef.current?.open) {
          comboboxRef.current.setOpen(false)
        }
      }
    }, [validItems])

    const autocomplete = (
      <Combobox.RootProvider
        value={combobox}
        className={cx("cobalt-Autocomplete", className, {
          "cobalt-Autocomplete--empty": validItems.length === 0,
        })}
        key={`${autocompleteKey}`}
      >
        {label && (
          <Combobox.Label className="cobalt-FormField__Label">
            {label}
          </Combobox.Label>
        )}
        <Combobox.Control
          className={cx("cobalt-TextField", {
            "cobalt-TextField--error": status === "error",
            "cobalt-TextField--success": status === "success",
            "cobalt-TextField--withIcon": icon,
            "cobalt-TextField--withValue":
              inputRef.current && inputRef.current.value.length > 0,
          })}
        >
          <Combobox.Input
            className="cobalt-TextField__Input"
            ref={inputRef}
            disabled={disabled}
            defaultValue={defaultInputValue}
            {...inputProps}
            onKeyDown={(e) => {
              onKeyDown?.(e)
              if (
                onSelectItem &&
                e.keyCode === KEY_CODE_ENTER &&
                combobox.highlightedItem
              ) {
                const processSelection = onSelectItem(
                  combobox.highlightedItem,
                  combobox.inputValue,
                )

                if (!processSelection) {
                  e.preventDefault()
                  e.stopPropagation()
                }
              }
            }}
          />
          {icon && (
            <Icon
              source={icon}
              color="primary"
              className="cobalt-TextField__Icon"
            />
          )}
          {value.length > 0 && !disabled && (
            <Combobox.ClearTrigger
              className="cobalt-Autocomplete__clear-button"
              data-testid="clear"
              onClick={() => {
                setDefaultInputValue("")
                setValue("")
                setAutocompleteKey(!autocompleteKey)
                minQueryLength === 0 &&
                  setTimeout(() => {
                    inputRef.current?.click()
                  }, 0)
                onClearValue?.()
                onQueryChange?.("")
              }}
            >
              <Icon source="close" size={16} />
            </Combobox.ClearTrigger>
          )}
        </Combobox.Control>
        <Portal>
          <Combobox.Positioner className="cobalt-Autocomplete__positioner">
            <Combobox.Content
              className={cx("cobalt-Autocomplete__content", popoverClassName, {
                "cobalt-Autocomplete__content--empty": validItems.length === 0,
              })}
            >
              {validItems.map((item, index) => (
                <Combobox.Item
                  key={`${item.label}-${item.value}-${index}`}
                  item={item}
                  className="cobalt-Autocomplete__item"
                >
                  {renderItem ? (
                    <div
                      {...getA11yOnClick((e) => {
                        if (onSelectItem) {
                          const processSelection = onSelectItem(
                            item,
                            combobox.inputValue,
                          )

                          if (!processSelection) {
                            e.preventDefault()
                            e.stopPropagation()
                          }
                        }
                      })}
                    >
                      {renderItem(item, combobox.inputValue)}
                    </div>
                  ) : (
                    <div
                      className={cx("cobalt-Autocomplete__item-wrapper", {
                        "cobalt-Autocomplete__item-wrapper--disabled":
                          item.disabled,
                      })}
                    >
                      {icon && (
                        <span className="cobalt-Autocomplete__item-icon">
                          <Icon source={icon} color="primary" />
                        </span>
                      )}
                      <Combobox.ItemText className="cobalt-Autocomplete__item-label">
                        {item.label}
                      </Combobox.ItemText>
                      <Combobox.ItemIndicator className="cobalt-Autocomplete_selected-item-indicator">
                        <CheckIcon size={16} />
                      </Combobox.ItemIndicator>
                    </div>
                  )}
                </Combobox.Item>
              ))}
            </Combobox.Content>
          </Combobox.Positioner>
        </Portal>
      </Combobox.RootProvider>
    )

    return label || hint ? (
      <div
        className={cx("cobalt-FormField", {
          "cobalt-FormField--withHint": hint,
          "cobalt-FormField--fullWidth": fullWidth,
        })}
      >
        {autocomplete}
        {hint && (
          <Hint status={status}>
            {/** biome-ignore lint/security/noDangerouslySetInnerHtml: Allow to pass content from the HAML template*/}
            <span dangerouslySetInnerHTML={{ __html: hint }} />
          </Hint>
        )}
      </div>
    ) : (
      autocomplete
    )
  },
)

_Autocomplete.displayName = "Autocomplete"

const Autocomplete = _Autocomplete as <
  T extends object = Record<string, unknown>,
>(
  props: Props<T> & React.RefAttributes<AutocompleteRefType<T>>,
) => React.ReactElement | null

export { Autocomplete }
